/**
 * File: useDuplicateWarningController.ts
 * Path: apps/admin/src/shared/hooks/useDuplicateWarningController.ts
 * Description: Finds leads and clients that look like the details typed (never the lead being edited, never our own phone or email).
 */

import { useMemo } from "react";
import { ADMIN_PATHS, findLookalikes, type MatchDetails } from "@idk/config";
import { useClientsList } from "./useClientsQueries";
import { useLeadsList } from "./useLeadsQueries";

export const useDuplicateWarningController = (details: MatchDetails, ignoreLeadId?: string) => {
  const leads = useLeadsList();
  const clients = useClientsList();
  const { phone, email, website, name } = details;

  return useMemo(() => {
    const candidate = { name, phone, email, website };
    const fromLeads = findLookalikes(candidate, leads.data ?? [], ignoreLeadId).map((m) => ({
      key: `lead-${m.record.id}`,
      kind: "lead" as const,
      name: m.record.name,
      href: ADMIN_PATHS.businesses.detail(m.record.id),
      reasons: m.reasons,
    }));
    const fromClients = findLookalikes(candidate, clients.data ?? []).map((m) => ({
      key: `client-${m.record.id}`,
      kind: "client" as const,
      name: m.record.name,
      href: ADMIN_PATHS.businesses.detail(m.record.id),
      reasons: m.reasons,
    }));
    return [...fromLeads, ...fromClients];
  }, [name, phone, email, website, leads.data, clients.data, ignoreLeadId]);
};
